import { Link } from 'react-router-dom';
import { Menu, X, Coins } from 'lucide-react';
import { LogoMark } from '@/components/ui/LogoMark';
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useGuest } from '@/context/GuestContext';
import { UserMenu } from '@/components/auth/UserMenu';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { LanguageToggle } from '@/components/layout/LanguageToggle';
import { buttonClasses } from '@/components/ui/Button';
import { api } from '@/lib/api';
import { useChatStore } from '@/stores/chatStore';
import { useLanguageStore } from '@/stores/languageStore';
import { t, type TranslationKey } from '@/lib/translations';
import { useSite } from '@/lib/useSite';

/** App features — shown only to signed-in users and guests (people who can actually use them). */
const NAV_ITEMS: { labelKey: TranslationKey; href: string }[] = [
  { labelKey: 'myJourney',     href: '/dashboard' },
  { labelKey: 'myKundali',     href: '/kundali' },
  { labelKey: 'predictions',   href: '/predictions' },
  { labelKey: 'reports',       href: '/reports' },
  { labelKey: 'compatibility', href: '/compatibility' },
  { labelKey: 'dosha',         href: '/dosha' },
  { labelKey: 'remedies',      href: '/remedies' },
];

/** Public, crawlable destinations — shown to anonymous visitors instead of disabled app links. */
const PUBLIC_NAV_ITEMS: { key: 'tools' | 'astrology' | 'blog' | 'pricing' | 'about'; href: string }[] = [
  { key: 'tools',     href: '/tools' },
  { key: 'astrology', href: '/astrology' },
  { key: 'blog',      href: '/blog' },
  { key: 'pricing',   href: '/pricing' },
  { key: 'about',     href: '/about' },
];

const DESKTOP_LINK_CLS =
  'px-1 lg:px-2 py-1.5 text-xs lg:text-sm font-medium rounded-md transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';
const MOBILE_LINK_CLS =
  'px-3 py-2 text-sm font-medium rounded-md hover:bg-accent hover:text-accent-foreground transition-colors';

export function Header() {
  const { isSignedIn, isLoaded } = useAuth();
  const { isGuest, creditsRemaining: guestCredits, guestName } = useGuest();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const storeCredits    = useChatStore((s) => s.creditsRemaining);
  const setStoreCredits = useChatStore((s) => s.setCredits);
  const [initialCredits, setInitialCredits] = useState<number | null>(null);
  const language = useLanguageStore((s) => s.language);
  // Public chrome follows the URL's language (a /hi/ page is Hindi whatever the
  // app preference says); the signed-in app nav keeps following the preference.
  const { s: site, path } = useSite();

  useEffect(() => {
    if (!isSignedIn) {
      setInitialCredits(null);
      return;
    }
    api.getCreditsBalance().then((res) => {
      if (res.success && res.data) {
        setInitialCredits(res.data.available);
        setStoreCredits(res.data.available);
      }
    });
  }, [isSignedIn, setStoreCredits]);

  const credits = storeCredits ?? initialCredits;
  /** Show the app feature nav only to people who can use it; everyone else sees public content. */
  const showAppNav = !!isSignedIn || isGuest;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-white/5 bg-transparent backdrop-blur-md">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">

        {/* ── Logo ── */}
        <Link to={path('/')} className="flex items-center gap-2 font-display font-semibold text-lg text-white">
          <LogoMark size={34} />
          Kundali <span className="text-gold-400">AI</span>
        </Link>

        {/* ── Desktop Navigation ── */}
        <nav className="hidden md:flex items-center gap-1">
          {showAppNav ? (
            <>
              <Link to="/chat" className={DESKTOP_LINK_CLS}>{t('chat', language)}</Link>
              {NAV_ITEMS.map(({ labelKey, href }) => (
                <Link key={href} to={href} className={DESKTOP_LINK_CLS}>
                  {t(labelKey, language)}
                </Link>
              ))}
            </>
          ) : (
            PUBLIC_NAV_ITEMS.map(({ key, href }) => (
              <Link key={href} to={path(href)} className={DESKTOP_LINK_CLS}>
                {site.nav[key]}
              </Link>
            ))
          )}
        </nav>

        {/* ── Auth / Credits Section ── */}
        <div className="hidden md:flex items-center gap-2">
          <LanguageToggle />
          {/* Render unconditionally so the anonymous CTA is present in prerendered HTML;
              swaps to the signed-in / guest UI once auth resolves client-side. */}
          {isSignedIn ? (
                <>
                  {credits !== null && (
                    <Link
                      to="/pricing"
                      aria-label={`${credits} ${t('credits', language)}`}
                      className="flex items-center gap-1 rounded-md px-1.5 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      <Coins className="h-4 w-4 text-yellow-500" />
                      {credits}
                    </Link>
                  )}
                  <NotificationBell />
                  <UserMenu />
                </>
              ) : isGuest ? (
                <>
                  <Link
                    to="/sign-up"
                    aria-label={`${guestCredits} ${t('credits', language)}`}
                    className="flex items-center gap-1 rounded-md px-1.5 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <Coins className="h-4 w-4 text-amber-400" />
                    {guestCredits}
                  </Link>
                  <span className="text-sm text-muted-foreground">{guestName}</span>
                  <Link to="/sign-up" className={buttonClasses({ variant: 'gold', size: 'sm' })}>
                    {t('signUp', language)}
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/sign-in" className={buttonClasses({ variant: 'ghost', size: 'sm' })}>
                    {site.nav.signIn}
                  </Link>
                  <Link to="/chat" className={buttonClasses({ variant: 'gold', size: 'sm' })}>
                    {site.nav.startFree}
                  </Link>
                </>
              )}
        </div>

        {/* ── Mobile Hamburger ── */}
        <button
          className="md:hidden p-2 rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          aria-label={site.nav.toggleMenu}
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* ── Mobile Menu ── */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border/40 bg-background">
          <div className="container mx-auto px-4 py-3 flex flex-col gap-1">

            {/* Language toggle */}
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-sm font-medium text-muted-foreground">{t('language', language)}</span>
              <LanguageToggle />
            </div>

            {/* Credits badge (signed-in users) */}
            {isSignedIn && credits !== null && (
              <Link
                to="/pricing"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              >
                <Coins className="h-4 w-4 text-yellow-500" />
                {credits} {t('credits', language)}
              </Link>
            )}

            {/* Credits badge (guest users) */}
            {isGuest && (
              <Link
                to="/sign-up"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              >
                <Coins className="h-4 w-4 text-amber-400" />
                {guestCredits} {t('credits', language)}
                {guestName && (
                  <span className="ml-1 text-muted-foreground/70">({guestName})</span>
                )}
              </Link>
            )}

            {/* Navigation: app features for signed-in/guest, public content for anonymous */}
            {showAppNav ? (
              <>
                <Link to="/chat" className={MOBILE_LINK_CLS} onClick={() => setMobileMenuOpen(false)}>
                  {t('chat', language)}
                </Link>
                {NAV_ITEMS.map(({ labelKey, href }) => (
                  <Link key={href} to={href} className={MOBILE_LINK_CLS} onClick={() => setMobileMenuOpen(false)}>
                    {t(labelKey, language)}
                  </Link>
                ))}
              </>
            ) : (
              PUBLIC_NAV_ITEMS.map(({ key, href }) => (
                <Link key={href} to={path(href)} className={MOBILE_LINK_CLS} onClick={() => setMobileMenuOpen(false)}>
                  {site.nav[key]}
                </Link>
              ))
            )}

            {/* Auth buttons */}
            {isLoaded && !isSignedIn && (
              <div className="flex flex-col gap-2 pt-3 mt-2 border-t border-border/40">
                {isGuest ? (
                  <Link to="/sign-up" onClick={() => setMobileMenuOpen(false)} className={buttonClasses({ variant: 'gold', size: 'sm' })}>
                    {t('signUp', language)}
                  </Link>
                ) : (
                  <>
                    <Link to="/chat" onClick={() => setMobileMenuOpen(false)} className={buttonClasses({ variant: 'gold', size: 'sm' })}>
                      {site.nav.startFree}
                    </Link>
                    <Link to="/sign-in" onClick={() => setMobileMenuOpen(false)} className={buttonClasses({ variant: 'outline', size: 'sm' })}>
                      {site.nav.signIn}
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
